import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  TextField,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { type DocumentApproval, cancelApproval } from '../../services/documentApprovalApi';
import { getCentralDbUrls } from '../../services/centralDbClient';

const statusColor = (s: string) => {
  switch (s) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getWorkerName(doc: DocumentApproval): string {
  const w = doc.worker;
  if (!w) return doc.workerId;
  return [w.firstName, w.prefix, w.lastName].filter(Boolean).join(' ');
}

interface Props {
  open: boolean;
  onClose: () => void;
  approval: DocumentApproval | null;
  accessToken: string;
  onCancelled: () => void;
}

export const ApprovalDetailDialog: React.FC<Props> = ({
  open,
  onClose,
  approval,
  accessToken,
  onCancelled,
}) => {
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!approval) return null;

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);
    try {
      await cancelApproval(accessToken, approval.id);
      onCancelled();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const fileUrl = approval.fileUrl
    ? approval.fileUrl.startsWith('http')
      ? approval.fileUrl
      : `${getCentralDbUrls().origin}${approval.fileUrl}`
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>{approval.title}</span>
          <Chip
            label={approval.status}
            color={statusColor(approval.status) as any}
            size="small"
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Meta info */}
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <div>
              <Typography variant="caption" color="text.secondary">Worker</Typography>
              <Typography variant="body2">{getWorkerName(approval)}</Typography>
              {approval.worker?.email && (
                <Typography variant="caption" color="text.secondary">{approval.worker.email}</Typography>
              )}
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Document Type</Typography>
              <Typography variant="body2">{approval.documentType.replace(/_/g, ' ')}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Created</Typography>
              <Typography variant="body2">{formatDate(approval.createdAt)}</Typography>
            </div>
            {approval.expiresAt && (
              <div>
                <Typography variant="caption" color="text.secondary">Expires</Typography>
                <Typography variant="body2">{formatDate(approval.expiresAt)}</Typography>
              </div>
            )}
          </Stack>

          {/* Magic link */}
          {approval.magicLinkToken ? (() => {
            const workerAppUrl = (import.meta.env.VITE_WORKER_APP_URL || 'http://localhost:5181').replace(/\/$/, '');
            const approvalUrl = `${workerAppUrl}/auth/verify?token=${encodeURIComponent(approval.magicLinkToken)}&redirect=${encodeURIComponent(`/documents/${approval.id}`)}`;
            return (
              <>
                <Divider />
                <div>
                  <Typography variant="caption" color="text.secondary">Magic Link</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      value={approvalUrl}
                      slotProps={{ input: { readOnly: true } }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => navigator.clipboard.writeText(approvalUrl)}
                      title="Copy to clipboard"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </div>
              </>
            );
          })() : approval.status === 'pending' && (
            <>
              <Divider />
              <Alert severity="warning">
                No magic link was generated for this approval. The worker has not received an email and cannot access this document.
                Delete this approval and create a new one to retry.
              </Alert>
            </>
          )}

          {approval.description && (
            <>
              <Divider />
              <div>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2">{approval.description}</Typography>
              </div>
            </>
          )}

          {/* Status-specific info */}
          {approval.status === 'approved' && approval.approvedAt && (
            <>
              <Divider />
              <div>
                <Typography variant="caption" color="text.secondary">Approved at</Typography>
                <Typography variant="body2">{formatDate(approval.approvedAt)}</Typography>
              </div>
            </>
          )}

          {approval.status === 'rejected' && (
            <>
              <Divider />
              <Stack spacing={1}>
                <div>
                  <Typography variant="caption" color="text.secondary">Rejected at</Typography>
                  <Typography variant="body2">{formatDate(approval.rejectedAt)}</Typography>
                </div>
                {approval.rejectionReason && (
                  <div>
                    <Typography variant="caption" color="text.secondary">Reason</Typography>
                    <Typography variant="body2">{approval.rejectionReason}</Typography>
                  </div>
                )}
              </Stack>
            </>
          )}

          {/* Signature */}
          {approval.signatureData && (
            <>
              <Divider />
              <div>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Signature
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    bgcolor: '#fff',
                    maxWidth: 400,
                  }}
                >
                  <img
                    src={approval.signatureData}
                    alt="Worker signature"
                    style={{ maxWidth: '100%', maxHeight: 150 }}
                  />
                </Box>
              </div>
            </>
          )}

          {/* Document content */}
          <Divider />
          <div>
            <Typography variant="subtitle2" gutterBottom>
              Document Content
            </Typography>
            {approval.htmlContent && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: 'auto',
                  bgcolor: '#fff',
                }}
              >
                <iframe
                  srcDoc={approval.htmlContent}
                  title="Document content"
                  sandbox=""
                  style={{ width: '100%', height: 400, border: 'none' }}
                />
              </Box>
            )}
            {fileUrl && (
              <Button
                variant="outlined"
                size="small"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open PDF
              </Button>
            )}
            {!approval.htmlContent && !fileUrl && (
              <Typography variant="body2" color="text.secondary">No document content available.</Typography>
            )}
          </div>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {approval.status === 'pending' && (
          <Button
            color="error"
            onClick={handleCancel}
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} /> : undefined}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Approval'}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
